import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Link from '@mui/material/Link';
import { LexicalEditor } from 'lexical';

import {
  CasBaseDialog,
  CasButton,
  CasConfirmationDialog,
  CasLoaderWithBackdrop,
  CasRHFLexicalRichTextEditor,
  CasRHFTextField,
} from 'src/compass/shared/components';
import { CasSecondaryButton } from 'src/compass/shared/components/CasButton/CasSecondaryButton';
import CasItemNavigator from 'src/compass/shared/components/CasItemNavigator/CasItemNavigator';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import CasReactHookForm, {
  FormMethodType,
} from 'src/compass/shared/components/CasForm/CasReactHookForm';
import {
  editorToEmailHtml,
  htmlToEditorState,
} from 'src/compass/shared/components/CasLexicalRichTextEditor/lexical-playground/src/CasLexicalRichTextEditor';
import { PENDING_EMAILS_QUERY_KEY } from 'src/compass/shared/components/CasPendingEmails/usePendingEmails';
import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import { Terms } from 'src/compass/shared/util/terms';
import { IGeneralObjectType } from 'src/compass/site/util';
import {
  BATCH_CHANNEL_OPTIONS,
  BatchOpenRowWatcher,
  BatchRowList,
  BatchRowPane,
  BatchSmsAdjustedType,
  BatchSmsAdjustmentsAlert,
  CustomerContactsButton,
  DEFAULT_BODY_LABEL,
  DEFAULT_KEY,
  DEFAULT_MESSAGE_FIELDS,
  FIRST_CHANNEL_PARAM,
  IBatchRow,
  IBatchTerms,
  MAX_SMS_TEXT_LENGTH,
  MAX_SUBJECT_LENGTH,
  MySignatureSettingsDialog,
  RHFMessageCategory,
  RecipientOptionType,
  RowChannelType,
  SendAsType,
  SendChannelType,
  SmsSnapshotType,
  SmsTextField,
  addressesFor,
  batchComposeSchema,
  defaultCountsFor,
  getRowChannel,
  isRecipientsEdited,
  makeBodiesSmsSpamSafe,
  makeSmsSpamSafe,
  replaceBodyWithSms,
  smsChangedSince,
  splitBodyForSms,
  supportsChannel,
  toQueryPairs,
  withoutKey,
  BatchSmsPictures,
  TEXT_NEEDS_MESSAGE_OR_PICTURE,
  findEmptyTexts,
  findOversizedMessages,
  findTooLongRow,
  toTransport,
  RingCentralConnectAlert,
  RingCentralFromNumber,
  TestingSiteNote,
  useConnectRingCentral,
  useRingCentralConnection,
  useBatchListView,
} from 'src/compass/application';

/*
  Batch Send from the Customers pages: one authored message, one copy per customer in the listing.

  The counterpart of SCE Reports' Batch Send, and built on the same shared parts - the row list,
  the row pane, the channel rules, the spam-safe confirmation. What it does NOT have is everything
  SCE Reports' batch carries because it is mailing a REPORT: no templates, no data objects, and no
  per-insured stamping of the body. A customer message is the sender's own words, sent to more
  than one customer at a time.
*/

// A pinned Send As, as the Defaults endpoint names the channel. Auto asks for no channel at all.
const CHANNEL_PARAM: Record<string, string | undefined> = {
  auto: undefined,
  email: 'Email',
  sms: 'Text',
};

// What a row stands for here, in the words this page uses - see IBatchTerms.
const CUSTOMER_TERMS: IBatchTerms = {
  noun: Terms.Customer.label.toLowerCase(),
  // Every listed row is a customer, so this cannot happen - stated for completeness rather than
  // because it is reachable.
  missingId: `No ${Terms.Customer.label.toLowerCase()} record for this row`,
  noContacts: 'No contact is included by default for this customer',
  contactsLabel: 'Contacts',
  emptyList: 'No customers match the current filters.',
};

/*
  A shared-service customer has more than one Risk Manager, and each of them can mail it. A batch
  reaches every customer once only when it is one RMC's customers that the RMC is the Service Owner
  of (Customers tab: an RMC picked and Is Service Owner = Yes).
*/
function duplicateWarning(listParams: IGeneralObjectType) {
  if (listParams.employeeId == null) return 'List of customers includes multiple Risk Managers';
  if (listParams.isRMCOfRecord !== true)
    return (
      'Messages may be duplicated for shared service customers if the other RMC sends the same ' +
      'message. (To prevent set "Is Service Owner" filter to Yes to only send to customers whose ' +
      'service you own.)'
    );
  return null;
}

interface Props {
  onClose: VoidFunction;
  // The listing's own url + filters, so the batch mails exactly the rows on screen.
  requestMetadata: IGeneralObjectType;
}

export default function CustomerBatchSendDialog({ onClose, requestMetadata }: Props) {
  const queryClient = useQueryClient();
  const signatureDialog = useCasBoolean();
  const confirmDialog = useCasBoolean();

  // Texts go out from the sender's own RingCentral line; while it is not connected, a batch with
  // any text in it cannot be sent. The rows keep their SMS badges, so the sender can see which.
  const { connected: ringCentralConnected } = useRingCentralConnection();

  const [formMethods, setFormMethods] = useState<FormMethodType>(null);
  const [masterEditor, setMasterEditor] = useState<LexicalEditor | null>(null);
  const [overrideEditor, setOverrideEditor] = useState<LexicalEditor | null>(null);

  /*
    Which view of the batch's message is on screen: its email Body, or its text. Either way it is ONE
    message, as a single send is: the view being edited is the message, and the other is translated
    from it (syncSmsFromBody / syncBodyFromSms), so the email and the text can never say different
    things.

    A message's channel still comes from its own row, but this decides where a contact with no
    Preferred Channel is reached (see FIRST_CHANNEL_PARAM) - so it settles every row whose contacts
    leave that open. It starts on SMS here; SCE Reports' Batch Send starts on Email.
  */
  const [batchChannel, setBatchChannel] = useState<SendChannelType>('sms');

  /*
    A row's own Send As, when the sender has pinned one. Keyed by row like the other row maps, and
    absent means Auto - resolve from that row's recipients. Pinning is how an unresolved row gets
    settled without editing anybody's contacts.
  */
  const [rowChannels, setRowChannels] = useState<Record<string, SendAsType>>({});

  /*
    The open row's recipients as they stand in the form, reported by the watcher inside it. Held
    only so the row's badge can follow the sender's edits: everything else - what is sent, what
    blocks the send - still reads the committed recipients, which is what a row is left holding.
  */
  const [openRow, setOpenRow] = useState<{
    rowKey: string;
    to: RecipientOptionType[];
    cc: RecipientOptionType[];
  } | null>(null);

  /*
    What Send is about to do - computed on Send, sent on confirm: the text bodies, and those over
    the carrier's length cap. The spam-safe rewrites are not among them: Send makes those in place
    and stops (see prepareSend), so by the time the confirmation opens there are none left to make.
  */
  const [pendingSend, setPendingSend] = useState<{
    batchText: string;
    usesBatchText: boolean;
    rowTexts: Record<string, string>;
    tooLong: { label: string; length: number }[];
    // Messages with more pictures than their channel can carry - these block the send too.
    tooLarge: { label: string; problem: string }[];
    // Texts with no words and nothing attached - and these.
    empty: string[];
  } | null>(null);

  /*
    What the SMS view was filled with when it opened - or last carried back into the email Body -
    and null while the Email view is on screen. Compared with the view's fields to tell whether the
    sender changed anything there: only then is the Body rewritten from it, so merely looking at the
    SMS view never costs the Body its formatting.
  */
  const smsDerived = useRef<SmsSnapshotType | null>(null);

  // The same, for the open row's Custom message while it is edited as a text (see BatchRowPane).
  const rowSmsDerived = useRef<SmsSnapshotType | null>(null);

  // What the last Send reworded for the carriers, by message - on screen until dismissed.
  const [smsAdjusted, setSmsAdjusted] = useState<BatchSmsAdjustedType[]>([]);

  /*
    Each text Send reworded, by message (DEFAULT_KEY or a row's key), with the text it was made from.

    The rewrite is the texts' alone - the emails keep the sender's "$" - so the bodies the texts are
    derived from still say what they said. Whenever a text is derived again from a body that has not
    changed since, the reworded one is what comes out: otherwise opening a message to review it would
    undo its rewrite, and the next Send would only make it again.
  */
  const smsRewrites = useRef<Record<string, { from: string; to: string }>>({});
  const rewritten = (key: string, text: string) =>
    smsRewrites.current[key]?.from === text ? smsRewrites.current[key].to : text;

  // The row's default contacts, fetched only when a row is opened. Separate from customRecipients
  // so "opened a row" and "edited its recipients" stay distinguishable - only the latter is sent
  // explicitly, and only the latter marks the row Custom.
  const [loadedRecipients, setLoadedRecipients] = useState<Record<string, { to: any[]; cc: any[] }>>(
    {}
  );
  /*
    Recipients the sender added by hand, held across a channel change.

    Pinning a channel re-resolves the row's DEFAULT contacts on it (see handleRowSendAsChange), and
    that answer arrives from the server a moment later - so anyone the sender had added themselves
    has to be parked here until it lands, or the reseed would wipe them. The reseed adds them to the
    defaults once and clears this; from then on the To/CC boxes are the only record of them.
  */
  const [keepOnReload, setKeepOnReload] = useState<{
    row: string;
    to: RecipientOptionType[];
    cc: RecipientOptionType[];
  } | null>(null);

  // Bumped when the open row's contacts are edited; the only signal RHFMessageRecipients takes to
  // refetch its address list.
  const [contactsUpdate, setContactsUpdate] = useState<Date | null>(null);
  // Latest-value mirror, read by the form subscription below - see the comment there.
  const loadedRecipientsRef = useRef(loadedRecipients);
  loadedRecipientsRef.current = loadedRecipients;

  // The rows taken out of the batch. Stored as exclusions so every row starts included without an
  // effect to seed it (see `included` below).
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [selectedKey, setSelectedKey] = useState<string>(DEFAULT_KEY);
  // Per-row message overrides, and per-row recipient edits. Kept apart because a row can have its
  // own To/Cc while still sending the batch's message. A row missing from BOTH maps is sent exactly
  // as the batch defines it, and its recipients are resolved server-side - which is why neither map
  // is pre-populated.
  const [overrides, setOverrides] = useState<
    Record<string, { subject: string; content: string; textContent: string }>
  >({});
  const [customRecipients, setCustomRecipients] = useState<Record<string, { to: any[]; cc: any[] }>>(
    {}
  );

  // Paging is the listing's, not the batch's: a batch mails every customer the filters match.
  const listParams = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { pageSize, pageNumber, ...rest } = requestMetadata?.params ?? {};
    return rest;
  }, [requestMetadata]);
  const listWarning = duplicateWarning(listParams);

  const { data: customers, isLoading, isError, error } = useRetrieve({
    url: 'Utilities/CustomerMessage/Batch/Customers',
    params: listParams,
  });

  /*
    The endpoint answers in this page's own words (entityId/customerName); the batch components
    speak the neutral row shape, so the mapping happens once, here.
  */
  const rows: IBatchRow[] = useMemo(
    () =>
      (customers?.result ?? []).map((customer: any) => ({
        key: String(customer.entityId),
        label: customer.customerName,
        id: customer.entityId,
        ...defaultCountsFor(customer, batchChannel),
        emailReachableCount: customer.emailReachableCount,
        phoneReachableCount: customer.phoneReachableCount,
        unusableAddresses: customer.unusableAddresses,
      })),
    [customers, batchChannel]
  );

  const rowKeys = useMemo(() => rows.map((r) => r.key), [rows]);

  const included = useMemo(
    () => new Set(rowKeys.filter((key) => !excluded.has(key))),
    [rowKeys, excluded]
  );

  const selectedRow = selectedKey === DEFAULT_KEY ? null : selectedKey;

  /*
    What the recipient field is seeded with - and the ONLY thing it can be seeded with.

    RHFMessageRecipients re-seeds to/cc from these arrays whenever their identity changes, so
    anything the field should hold has to be in the row maps themselves - which is why the sender's
    own additions kept across a channel change are stored in customRecipients when the defaults
    land (see the reseed below), not merged in here. Merged here, they were re-applied every time
    either map changed (a Send, Edit Contacts), and a recipient the sender had removed came back.

    Stored arrays, never fresh literals: a new array each render would re-seed continuously and
    clobber whatever the sender just typed.
  */
  const emptyRecipients = useRef({ to: [] as any[], cc: [] as any[] }).current;
  const rowRecipients = selectedRow
    ? customRecipients[selectedRow] ?? loadedRecipients[selectedRow] ?? emptyRecipients
    : emptyRecipients;

  /*
    Which channel each row's message goes on, or why it has none yet.

    Resolved here rather than in the list item because it depends on the dialog's own row maps - a
    pinned Send As, and the recipients of a row the user edited. A row is judged on its edited
    recipients when it has them, and otherwise on the per-channel counts the list endpoint gave.
  */
  const resolvedRows = new Map<string, RowChannelType>(
    rows.map((row) => [
      row.key,
      getRowChannel(
        row,
        CUSTOMER_TERMS,
        rowChannels[row.key] ?? 'auto',
        /*
          Whatever is in the To/CC boxes is what the badge must agree with, so the freshest answer
          wins: the OPEN row's live recipients first, then the row's edits-or-defaults - for the
          open row, exactly what its boxes were seeded with (see rowRecipients), additions kept
          across a channel change included. The per-channel counts behind those are only a
          stand-in for rows nobody has opened, or whose addresses have not arrived yet - they were
          taken when the dialog opened, and neither editing a row's contacts nor selecting a
          different address refetches the list.
        */
        openRow?.rowKey === row.key
          ? { to: openRow.to, cc: openRow.cc }
          : customRecipients[row.key] ?? loadedRecipients[row.key]
      ),
    ])
  );

  const resolveRow = (row: IBatchRow) => resolvedRows.get(row.key)!;

  const unresolvedRows = rows.filter((r) => included.has(r.key) && !!resolveRow(r).reason);

  const selectedRowData = selectedRow ? rows.find((r) => r.key === selectedRow) ?? null : null;

  const rowContentRef = useRef<string>('');

  /*
    The open row's Custom state, live.

    overrides/customRecipients are only written when a row is left (see commitCurrent), which is
    correct for the body - Lexical's HTML is expensive to serialise on every keystroke - but it left
    the chip a step behind. So the flags that drive the chip are tracked separately and updated on
    every change, while the body is still only serialised on commit.

    Tagged with the row it describes. During a switch the subscription below still closes over the
    OUTGOING row while setValue pushes the INCOMING row's values through it, so an untagged flag
    would be computed from one row's values and read against another's - marking a pristine row
    Custom.
  */
  const [liveRow, setLiveRow] = useState<{
    row: string | null;
    useDefault: boolean;
    recipientsEdited: boolean;
  }>({ row: null, useDefault: true, recipientsEdited: false });

  useEffect(() => {
    if (!formMethods) return undefined;

    const subscription = formMethods.watch((values, { name }) => {
      // Mirrored out of RHF on every keystroke so committing a row never depends on RHF having
      // flushed by the time the click handler runs.
      if (name === 'rowContent') rowContentRef.current = values.rowContent ?? '';

      if (!selectedRow) return;

      // Through the ref, never the closure: re-seeding a row (a contacts edit refetches its
      // defaults) writes new defaults AND new form values in the same commit, and a subscription
      // still holding the previous defaults would compare the new addresses against the old ones
      // and label a freshly-defaulted row as edited.
      const useDefault = !!values.useDefault;
      const recipientsEdited = isRecipientsEdited(values, loadedRecipientsRef.current[selectedRow]);

      // Bail when nothing actually changed. This fires on every keystroke in the row's Subject and
      // Body, and a new object each time would re-render the whole row list behind it.
      setLiveRow((prev) =>
        prev.row === selectedRow &&
        prev.useDefault === useDefault &&
        prev.recipientsEdited === recipientsEdited
          ? prev
          : { row: selectedRow, useDefault, recipientsEdited }
      );
    });

    return () => subscription.unsubscribe();
  }, [formMethods, selectedRow]);

  /*
    What a row has of its own, reported as two independent facts rather than one "customized" flag:
    the list labels them separately (To/CC vs Msg) because they are separately undoable and answer
    different questions - "who is this going to" and "what does it say".
  */
  /*
    A pinned channel is reported separately from edited recipients, though both change who the
    message reaches: the list shows one or the other (see BatchRowList), and which one it is tells
    the sender whether they chose those addresses or merely chose the channel they are on.
  */
  const isChannelPinned = (key: string) => (rowChannels[key] ?? 'auto') !== 'auto';

  const getRowCustom = (key: string) =>
    key === selectedRow && key === liveRow.row
      ? {
          recipients: liveRow.recipientsEdited,
          message: !liveRow.useDefault,
          channel: isChannelPinned(key),
        }
      : {
          recipients: !!customRecipients[key],
          message: !!overrides[key],
          channel: isChannelPinned(key),
        };

  const selectedCustom = selectedRow ? getRowCustom(selectedRow) : null;

  // A row's default contacts, loaded the first time it is opened. Nothing is fetched for rows the
  // user never customizes - the server resolves those itself at send time.
  /*
    Addressed on the row's pinned channel when it has one, so the pane shows the addresses the send
    will actually use. On Auto there is no channel to impose and the contacts answer for themselves.
  */
  const pinnedChannel = selectedRow ? rowChannels[selectedRow] : undefined;
  // Undefined on Auto, which is what leaves the contacts to answer for themselves.
  const pinnedChannelParam = CHANNEL_PARAM[pinnedChannel ?? 'auto'];

  const { data: rowDefaults, refetch: refetchRowDefaults } = useRetrieve({
    url: `Utilities/CustomerMessage/Defaults/${selectedRow ?? 0}`,
    params: { channel: pinnedChannelParam, firstChannel: FIRST_CHANNEL_PARAM[batchChannel] },
    enabled: !!selectedRow && !loadedRecipients[selectedRow],
  });

  /*
    Keyed on the response rather than "have I loaded this row yet", so a refetch lands.

    Editing a row's contacts re-enables the query above, and with the app-wide staleTime of 0 that
    serves the cached (pre-edit) defaults first and refetches behind it. Bailing once the row was in
    loadedRecipients would leave the pane showing exactly that stale serve.
  */
  useEffect(() => {
    if (!selectedRow || !rowDefaults) return;

    const loaded = { to: rowDefaults.to ?? [], cc: rowDefaults.cc ?? [] };
    setLoadedRecipients((prev) => ({ ...prev, [selectedRow]: loaded }));

    /*
      A pinned channel's defaults: the recipients the sender added by hand, parked by
      handleRowSendAsChange, join them now - once. Stored as the row's own recipients and no longer
      parked, so from here on a removal, a CC added later and a contacts edit behave as on a row
      that was never pinned.
    */
    const keep = keepOnReload?.row === selectedRow ? keepOnReload : null;
    if (keep) {
      setKeepOnReload(null);

      // Deduped on the address, since someone the sender added by hand can also be one of the
      // defaults on the channel just pinned.
      const loadedIds = new Set(loaded.to.map((option: RecipientOptionType) => String(option.id)));
      const kept = {
        to: [...loaded.to, ...keep.to.filter((option) => !loadedIds.has(String(option.id)))],
        cc: keep.cc,
      };

      // Nothing left once deduped means the row is simply on its defaults, so it is not marked.
      if (isRecipientsEdited(kept, loaded)) {
        setCustomRecipients((prev) => ({ ...prev, [selectedRow]: kept }));
        formMethods?.setValue('to', kept.to);
        formMethods?.setValue('cc', kept.cc);
        return;
      }
    }

    // Only seed the form if this row is still the one on screen and the user has not already
    // edited it.
    if (!customRecipients[selectedRow]) {
      formMethods?.setValue('to', loaded.to);
      formMethods?.setValue('cc', loaded.cc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, rowDefaults]);

  const syncContentFromEditor = () => {
    if (!masterEditor || !formMethods) return;

    setTimeout(
      () =>
        formMethods.setValue('content', editorToEmailHtml(masterEditor), { shouldValidate: false }),
      0
    );
  };

  /*
    The SMS view filled from the email Body: its words, and its pictures as the text's attachments.
    On every visit, since the Body may have changed meanwhile - which loses nothing, because whatever
    the sender changed in the view was carried back into the Body when they left it.
  */
  const syncSmsFromBody = (): SmsSnapshotType | null => {
    if (!masterEditor || !formMethods) return null;

    const { text: derived, images } = splitBodyForSms(masterEditor);
    const text = rewritten(DEFAULT_KEY, derived);

    formMethods.setValue('textContent', text, { shouldValidate: true });
    formMethods.setValue('smsImages', images);

    return { text, images };
  };

  /*
    The other direction, and automatic: once the sender has changed anything in the SMS view, the
    email Body is rewritten from it - the words, then the pictures - so the emails say what the texts
    say. Done at each moment something reads the Body (leaving the view, opening a row, Send) rather
    than on every keystroke; the Body is out of sight meanwhile, so the sender sees no difference.
  */
  const syncBodyFromSms = () => {
    const derived = smsDerived.current;
    if (!masterEditor || !formMethods || !derived) return;

    const sms: SmsSnapshotType = {
      text: formMethods.getValues('textContent') ?? '',
      images: formMethods.getValues('smsImages') ?? [],
    };
    if (!smsChangedSince(sms, derived)) return;

    replaceBodyWithSms(masterEditor, sms);
    formMethods.setValue('content', editorToEmailHtml(masterEditor), { shouldValidate: false });
    smsDerived.current = sms;
  };

  const showBatchView = (view: SendChannelType) => {
    if (view === 'sms') smsDerived.current = syncSmsFromBody();
    else {
      syncBodyFromSms();
      smsDerived.current = null;
    }

    setBatchChannel(view);

    /*
      The rows opened so far had their defaults fetched for the other channel, which tries a
      contact's addresses in the other order. Dropped, so each is judged on the new channel's counts
      and fetched afresh when opened again. Recipients the sender edited are theirs, and stay.
    */
    setLoadedRecipients({});
  };

  /*
    The window opens on the SMS view, so it needs what showBatchView takes on the way in: the
    snapshot the view is compared against, without which what the sender writes there would never
    be carried into the email Body. Taken once the Body's editor is ready.
  */
  useEffect(() => {
    if (masterEditor && formMethods && batchChannel === 'sms' && !smsDerived.current) {
      smsDerived.current = syncSmsFromBody();
    }
    // Only on the way in; from then on showBatchView keeps the snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterEditor, formMethods]);

  const onBatchChannelChange = (
    _: React.MouseEvent<HTMLElement>,
    value: SendChannelType | null
  ) => {
    if (value && value !== batchChannel) showBatchView(value);
  };

  /*
    Pin (or unpin) the open row's channel.

    Pruning happens HERE rather than in an effect watching the channel: this form is one scratchpad
    shared by whichever row is open, so an effect would also fire as selectItem swaps the next row
    in and would prune the row that just left, using its predecessor's chips.
  */
  const handleRowSendAsChange = (sendAs: SendAsType) => {
    if (!selectedRow) return;

    setRowChannels((prev) => ({ ...prev, [selectedRow]: sendAs }));

    if (!formMethods) return;

    /*
      Pinning rebuilds the row's recipients rather than filtering them.

      The two halves are treated differently because they mean different things:

        * the DEFAULT contacts are re-resolved on the new channel. They were addressed on the
          channel each contact prefers, so merely filtering them to the pinned one throws all of
          them away and leaves the row empty - the row then sends to nobody, which is exactly what
          pinning was meant to prevent. Dropping the loaded set re-enables the query above, which
          now asks for this channel, and the effect below seeds the answer.

        * anyone the sender ADDED by hand is theirs, and is kept - minus whoever the new channel
          cannot reach, since there is no other address to fall back to for them. They are parked
          in keepOnReload because the re-resolved defaults arrive asynchronously and would
          otherwise overwrite them.

      A default the sender had REMOVED comes back, because the two channels address a contact
      differently and an option's id is its address, so there is nothing to match the removal
      against on the other side.
    */
    const values = formMethods.getValues();
    const defaultIds = new Set(
      (loadedRecipients[selectedRow]?.to ?? []).map((option: RecipientOptionType) => String(option.id))
    );

    const reachable = (list: RecipientOptionType[] = []) =>
      sendAs === 'auto' ? list : list.filter((option) => supportsChannel(option, sendAs));

    const addedTo = reachable(
      (values.to ?? []).filter((option: RecipientOptionType) => !defaultIds.has(String(option.id)))
    );
    // CC is never defaulted (DefaultInclude means To), so all of it is the sender's own.
    const keptCc = reachable(values.cc ?? []);

    setKeepOnReload({ row: selectedRow, to: addedTo, cc: keptCc });
    // Both maps, so the reseed below runs for a row whose edits were already committed too.
    setCustomRecipients((prev) => withoutKey(prev, selectedRow));
    setLoadedRecipients((prev) => withoutKey(prev, selectedRow));

    formMethods.setValue('to', addedTo);
    formMethods.setValue('cc', keptCc);
  };

  // Only on the Email view, where the editor's toolbar is - the SMS view is filled afresh on its
  // next visit.
  const clearBody = () => {
    if (masterEditor) {
      htmlToEditorState(masterEditor, '');
      syncContentFromEditor();
    }
  };

  /*
    The open row's SMS editor, filled from its Custom message - the row's own rich body, in the
    override editor - just as the email Body fills the batch's SMS view.
  */
  const syncRowSmsFromBody = () => {
    if (!overrideEditor || !formMethods) return;

    const { text: derived, images } = splitBodyForSms(overrideEditor);
    const text = selectedRow ? rewritten(selectedRow, derived) : derived;

    formMethods.setValue('rowTextContent', text);
    formMethods.setValue('rowSmsImages', images);
    rowSmsDerived.current = { text, images };
  };

  // And back, automatically, once the sender has changed anything there - see syncBodyFromSms.
  const syncRowBodyFromSms = () => {
    const derived = rowSmsDerived.current;
    // A row on the default message has no message of its own to carry anything into.
    if (!overrideEditor || !formMethods || !derived || formMethods.getValues('useDefault')) return;

    const sms: SmsSnapshotType = {
      text: formMethods.getValues('rowTextContent') ?? '',
      images: formMethods.getValues('rowSmsImages') ?? [],
    };
    if (!smsChangedSince(sms, derived)) return;

    replaceBodyWithSms(overrideEditor, sms);
    rowContentRef.current = editorToEmailHtml(overrideEditor);
    formMethods.setValue('rowContent', rowContentRef.current, { shouldValidate: false });
    rowSmsDerived.current = sms;
  };

  /*
    Whether a row goes out as a text - pinned to SMS, or on Auto with recipients that resolve to
    it - which is when its Custom message is edited in the SMS editor (see BatchRowPane). The same
    resolver that picks the channel the row is sent on, so the editor shown is the channel used.
  */
  const editedAsText = (row: IBatchRow) =>
    (rowChannels[row.key] ?? 'auto') === 'sms' || resolveRow(row).channel === 'sms';
  const rowSmsEditing = !!selectedRowData && editedAsText(selectedRowData);
  const rowCustomized = !!selectedRow && liveRow.row === selectedRow && !liveRow.useDefault;

  /*
    The open row's editor follows its channel: into the SMS editor when the row goes out as a text,
    and - carrying anything changed there into the row's message - back out when it no longer does.
    Read behind the override editor's pending update, so a row seeded a moment ago is read as
    seeded, not as the row before it.
  */
  useEffect(() => {
    // Not the instance that unmounted with the row pane: the one mounting now reports itself next.
    if (!overrideEditor?.getRootElement() || !formMethods) return;

    if (!rowCustomized) {
      rowSmsDerived.current = null;
      return;
    }

    if (rowSmsEditing) {
      if (!rowSmsDerived.current) overrideEditor.update(() => {}, { onUpdate: syncRowSmsFromBody });
      return;
    }

    syncRowBodyFromSms();
    rowSmsDerived.current = null;
    // The two syncs are render-scoped functions over the same editor and form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, rowSmsEditing, rowCustomized, overrideEditor, formMethods]);

  /*
    Stash whatever is on screen for the row being left.

    Called synchronously from the click handler, BEFORE the editor is repointed: htmlToEditorState
    clears the editor root, and its own update echoes back through RHF - so committing any later
    (in an effect keyed on the selection, say) would write the incoming row's body onto the
    outgoing row's key.

    Returns the overrides as committed, because the Send click reads them in the same handler:
    setOverrides only lands on the next render.
  */
  const commitCurrent = () => {
    if (!selectedRow || !formMethods) return overrides;

    // The row's SMS editor into its message first, while the override editor still holds this row.
    syncRowBodyFromSms();

    const values = formMethods.getValues();

    setCustomRecipients((prev) =>
      isRecipientsEdited(values, loadedRecipients[selectedRow])
        ? { ...prev, [selectedRow]: { to: values.to ?? [], cc: values.cc ?? [] } }
        : withoutKey(prev, selectedRow)
    );

    /*
      The same message as plain text, for a row that goes out as one: as the sender wrote it, when
      they wrote it in the row's SMS editor, and otherwise derived HERE, while the override editor
      still holds this row - splitBodyForSms needs the live LexicalEditor, not the HTML string
      below, so there is no deriving it later.

      Kept for every row, not just the SMS ones, because a row's channel can still change after it
      was written.
    */
    let textContent = '';
    if (rowSmsDerived.current) textContent = values.rowTextContent ?? '';
    else if (overrideEditor) textContent = splitBodyForSms(overrideEditor).text;

    const nextOverrides = values.useDefault
      ? withoutKey(overrides, selectedRow)
      : {
          ...overrides,
          [selectedRow]: {
            subject: values.rowSubject ?? '',
            content: rowContentRef.current,
            textContent,
          },
        };

    setOverrides(nextOverrides);

    return nextOverrides;
  };

  /*
    Point the (hidden) override editor at one row's message.

    An override starts from the batch's body - the point is to tweak this customer's copy, not to
    retype it. Unlike SCE Reports there is nothing to stamp into it: the body carries no per-row
    placeholders.
  */
  const seedOverride = (rowKey: string) => {
    const existing = overrides[rowKey];
    const body = existing?.content ?? formMethods?.getValues('content') ?? '';

    formMethods?.setValue('rowSubject', existing?.subject ?? formMethods?.getValues('subject'));
    rowContentRef.current = body;
    /*
      Into the form as well. Opened from the default message, the row pane mounts - and with it a
      NEW editor, which starts from this field rather than from the instance seeded below (that one
      went with the pane). Without it the row showed the body of whichever row was edited last.
    */
    formMethods?.setValue('rowContent', body);
    if (overrideEditor) htmlToEditorState(overrideEditor, body);
  };

  const selectItem = (nextKey: string) => {
    /*
      Re-selecting the open row is a no-op. Going through the switch below would re-seed the form
      from this render's maps, which do not yet hold the commit just queued.
    */
    if (nextKey === selectedKey) return;

    // Leaving the batch's own message: a row's Custom message starts as a copy of its email Body,
    // so the SMS view is carried back into that Body first.
    syncBodyFromSms();

    commitCurrent();
    // The next row's SMS editor is read afresh from its own message.
    rowSmsDerived.current = null;

    // The additions parked by a channel change belong to the row being left - see the reseed above.
    setKeepOnReload(null);

    if (nextKey !== DEFAULT_KEY) {
      const override = overrides[nextKey];
      const recipients = customRecipients[nextKey] ?? loadedRecipients[nextKey];

      formMethods?.setValue('to', recipients?.to ?? []);
      formMethods?.setValue('cc', recipients?.cc ?? []);
      formMethods?.setValue('useDefault', !override);

      // Only when the row actually has an override to show. Seeding eagerly meant a full HTML ->
      // Lexical parse on every click in the list, for an editor nobody is looking at; unchecking
      // "use default message" seeds it at the moment it becomes visible.
      if (override) seedOverride(nextKey);
      else formMethods?.setValue('rowSubject', formMethods?.getValues('subject'));

      // Last, so it wins over the setValue calls above - each of those fires the watch
      // subscription, which is still closed over the OUTGOING row.
      setLiveRow({
        row: nextKey,
        useDefault: !override,
        recipientsEdited: !!customRecipients[nextKey],
      });
    }

    setSelectedKey(nextKey);
  };

  /*
    selectItem closes over most of the dialog's state, so it is a new function every render. The row
    list is memoized on its props, so it takes this stable wrapper instead; the ref keeps it pointed
    at the current closure.
  */
  const selectItemRef = useRef(selectItem);
  selectItemRef.current = selectItem;
  const handleSelectItem = useCallback((key: string) => selectItemRef.current(key), []);

  // Unchecking "use default" starts the override from the batch's message rather than a blank
  // editor.
  const handleUseDefaultChange = (useDefault: boolean) => {
    formMethods?.setValue('useDefault', useDefault);
    if (!selectedRow) return;

    // Set alongside the subscription rather than relying on it: the effect re-subscribes on the
    // selection, so a toggle immediately after a switch could otherwise land on the old closure.
    setLiveRow((prev) => ({ ...prev, row: selectedRow, useDefault }));

    if (useDefault) return;

    seedOverride(selectedRow);
  };

  /*
    The open row's contacts were edited, so everything derived from the old ones is stale: the row's
    default To/Cc, and the address pick-list inside RHFMessageRecipients (which only refetches when
    its `update` prop changes identity).

    Dropping the row from customRecipients is deliberate: a contacts edit resets that row to the new
    defaults - the same rule the single-send dialog follows - so its To/CC label clears too.
  */
  const handleContactsChanged = () => {
    if (!selectedRow) return;

    setCustomRecipients((prev) => withoutKey(prev, selectedRow));

    // refetch() rather than dropping the row from loadedRecipients to re-enable the query: react-
    // query serves the cached (pre-edit) defaults on an enable-flip, and in the gap before the
    // network answers the pane would blank the To field.
    refetchRowDefaults();

    setContactsUpdate(new Date());
    setLiveRow((prev) => ({ ...prev, row: selectedRow, recipientsEdited: false }));
  };

  // Puts the row back exactly as it arrived - default message AND default recipients. Reverting
  // only the message would leave the row still Custom for edits the user cannot see from the list.
  const handleRevert = () => {
    if (!selectedRow) return;

    /*
      A pinned row's loaded defaults were addressed on the pinned channel (see
      handleRowSendAsChange), so back on Auto they are not its defaults any more. They are dropped,
      as the pin dropped the Auto ones, which re-enables the query above - keyed on Auto again - to
      seed the Auto defaults: at once from the cache while it still has them, and from the server
      either way. Kept, they left that query disabled with only the cache to answer it, and React
      Query drops a query 5 minutes after its last observer leaves - so a row reverted later than
      that kept the pinned channel's addresses while reading as untouched, and was sent on them.

      Until the Auto defaults arrive To/CC stay empty, as after a pin. A row never pinned already
      holds its Auto defaults, and gets them back at once.
    */
    const pinned = isChannelPinned(selectedRow);
    const loaded = pinned ? undefined : loadedRecipients[selectedRow];

    setOverrides((prev) => withoutKey(prev, selectedRow));
    setCustomRecipients((prev) => withoutKey(prev, selectedRow));
    if (pinned) setLoadedRecipients((prev) => withoutKey(prev, selectedRow));
    // Back to Auto too: "default" means everything this row overrode.
    setRowChannels((prev) => withoutKey(prev, selectedRow));
    // Including anyone a channel change had kept: reverting is back to the defaults alone.
    setKeepOnReload(null);

    formMethods?.setValue('useDefault', true);
    formMethods?.setValue('to', loaded?.to ?? []);
    formMethods?.setValue('cc', loaded?.cc ?? []);

    setLiveRow({ row: selectedRow, useDefault: true, recipientsEdited: false });
  };

  // A send refused because RingCentral is not connected starts connecting when OK is pressed.
  const { connectIfNotConnected } = useConnectRingCentral();

  const { mutate, isPending } = useAddMutation({
    url: 'Utilities/CustomerMessage/Batch/Send',
    onErrorDismiss: connectIfNotConnected,
    onSuccess: (response: any) => {
      onSuccessEnqueueSnackbar(response);
      queryClient.invalidateQueries({ queryKey: [PENDING_EMAILS_QUERY_KEY] });
      onClose();
    },
  });

  // Validated here rather than in the Yup schema: the schema only ever sees the form's own fields,
  // and this is about the row map beside it. A row the user opened and cleared must block the send -
  // distinct from a row never opened, which carries no addresses on purpose and is resolved
  // server-side.
  const emptiedRows = rows
    .filter((row) => {
      if (!included.has(row.key)) return false;

      const custom = customRecipients[row.key];
      const { channel } = resolveRow(row);

      // Counted after resolving addresses, not before: a row whose only recipients cannot be
      // reached on its channel is just as empty as one the user cleared. An unresolved row is
      // already blocking on its own account, so it is not counted twice.
      return custom && channel ? !addressesFor(custom.to, channel).length : false;
    })
    .map((row) => row.key);

  /*
    Both kinds of broken row under one count: the ones that cannot decide a channel, and the ones
    that decided but hold no address the channel can reach. They are disjoint by construction -
    getRowChannel answers with a channel or a reason, never both - so this is a plain sum.
  */
  const invalidRowCount = unresolvedRows.length + emptiedRows.length;

  // The message list's All / Invalid / Excluded views. Invalid is exactly the rows counted above.
  const {
    listView,
    changeListView,
    visibleRows,
    invalidCount,
    excludedCount,
    navigableKeys,
    formatNavLabel,
  } = useBatchListView({
    rows,
    included,
    invalidKeys: new Set([...unresolvedRows.map((row) => row.key), ...emptiedRows]),
    selectedKey,
    onSelect: handleSelectItem,
  });

  /*
    Every body that will actually leave as a text: the batch's own, if any included row falls back
    to it, plus each SMS row that carries its own. Only these are rewritten - an email body is not
    scored by a carrier.
  */
  const smsRows = rows.filter((row) => included.has(row.key) && resolveRow(row).channel === 'sms');

  const smsBlocked = smsRows.length > 0 && !ringCentralConnected;

  // Whether any included message goes out by email - the only kind with a Subject.
  const sendsEmail = rows.some(
    (row) => included.has(row.key) && resolveRow(row).channel === 'email'
  );

  // Takes the overrides commitCurrent just produced - see there for why state would be stale here.
  const prepareSend = (committedOverrides: typeof overrides) => {
    // A customized row's text is its own, words or not - one left with only its picture goes out
    // that way, as the server sends it. Only a row on the default message uses the batch's text.
    const ownText = (key: string) => committedOverrides[key]?.textContent ?? null;

    // Labelled by key while they are worked on - two customers can share a name - and by name only
    // where the sender reads them.
    const rowBodies = makeBodiesSmsSpamSafe(
      smsRows
        .filter((row) => ownText(row.key) !== null)
        .map((row) => ({ label: row.key, text: ownText(row.key)! }))
    );
    const labelOf = (key: string) => rows.find((row) => row.key === key)?.label ?? key;

    const usesBatchText = smsRows.some((row) => ownText(row.key) === null);

    /*
      The texts say what the email Body says. On the Email view the text is translated from the
      Body here - what makes it necessary is that a ROW is a text, not that the sender happened to
      open the SMS view. On the SMS view the text IS the message, as the sender left it; the Body
      was rewritten from it when Send was pressed.
    */
    if (usesBatchText && batchChannel === 'email') syncSmsFromBody();

    // setValue is synchronous, so the text derived above is readable here.
    const batchText = formMethods?.getValues('textContent') ?? '';
    const batch = usesBatchText ? makeSmsSpamSafe(batchText) : null;

    /*
      Reworded for the carriers in place, then stop - rather than in the confirmation, where the
      sender could only take the list on trust. Every text is rewritten where it lives (the SMS view,
      the open row's SMS editor, each row's stored text), the notice says which messages changed and
      how, and the sender opens any of them to look before pressing Send again. That Send has nothing
      left to reword, so it goes on to the confirmation.
    */
    if (batch?.adjustments.length || rowBodies.changed.length) {
      if (batch?.adjustments.length) {
        smsRewrites.current[DEFAULT_KEY] = { from: batchText, to: batch.text };
        formMethods?.setValue('textContent', batch.text, { shouldValidate: true });
        // Not an edit of the sender's, so it is not carried into the emails - see smsRewrites.
        if (smsDerived.current) smsDerived.current = { ...smsDerived.current, text: batch.text };
      }

      const nextOverrides = { ...committedOverrides };
      rowBodies.changed.forEach(({ label: key }) => {
        const to = rowBodies.bodies.find((body) => body.label === key)!.text;

        smsRewrites.current[key] = { from: ownText(key)!, to };
        nextOverrides[key] = { ...committedOverrides[key], textContent: to };

        // The open row's SMS editor is on screen. commitCurrent has just carried any edit made there
        // into the row's body, so its snapshot can take the rewrite without losing one.
        if (key === selectedRow) {
          formMethods?.setValue('rowTextContent', to, { shouldValidate: true });
          if (rowSmsDerived.current) rowSmsDerived.current = { ...rowSmsDerived.current, text: to };
        }
      });
      setOverrides(nextOverrides);

      setSmsAdjusted([
        ...(batch?.adjustments.length
          ? [{ label: DEFAULT_BODY_LABEL, adjustments: batch.adjustments }]
          : []),
        ...rowBodies.changed.map(({ label: key, adjustments }) => ({
          label: labelOf(key),
          adjustments,
        })),
      ]);
      return;
    }

    // The carrier's length cap, on the bodies as they will be sent - already reworded, if Send had
    // to, which lengthens them ("$1,000" becomes "1,000 dollars").
    const tooLong = [
      ...(usesBatchText ? [{ label: DEFAULT_BODY_LABEL, length: batchText.length }] : []),
      ...rowBodies.bodies.map((body) => ({ label: labelOf(body.label), length: body.text.length })),
    ].filter((body) => body.length > MAX_SMS_TEXT_LENGTH);

    const rowTexts = Object.fromEntries(rowBodies.bodies.map((body) => [body.label, body.text]));

    /*
      The rich body a customized row is sent from, which its pictures come from - as the server
      reads it. An email whose body was cleared goes out on the default's; a text is the row's own
      message even then, so it carries only its own pictures.
    */
    const ownBody = (row: IBatchRow) => {
      const override = committedOverrides[row.key];

      return override && (resolveRow(row).channel === 'sms' || override.content?.trim())
        ? override.content
        : undefined;
    };

    // Pictures are budgeted per message, on the channel it goes out on - see findOversizedMessages.
    const tooLarge = findOversizedMessages(
      rows
        .filter((row) => included.has(row.key))
        .map((row) => ({
          label: row.label,
          ownBody: ownBody(row),
          channel: resolveRow(row).channel ?? 'email',
        })),
      formMethods?.getValues('content') ?? ''
    );

    // Texts with no words and no picture to carry instead - the server refuses these too.
    const empty = findEmptyTexts(
      usesBatchText ? { text: batchText, body: formMethods?.getValues('content') ?? '' } : null,
      smsRows
        .filter((row) => ownText(row.key) !== null)
        .map((row) => ({ label: row.label, text: rowTexts[row.key], body: ownBody(row) ?? '' }))
    );

    setPendingSend({ batchText, usesBatchText, rowTexts, tooLong, tooLarge, empty });

    confirmDialog.setTrue();
  };

  const handleSend = async () => {
    const values = formMethods?.getValues();

    // Closed first: the pictures below take a moment, and the confirmation must not be pressed twice.
    confirmDialog.setFalse();

    const sending = rows.filter((row) => included.has(row.key));

    /*
      Every body in the batch in one pass, so a picture shared by the default message and the rows
      customized from it - each a copy of the default's body, pictures and all - is sent once. A
      text's pictures are among them: it attaches them from the rich body it would be sent from.
    */
    const {
      bodies: [content, ...rowContents],
      images,
    } = await toTransport([values?.content, ...sending.map((row) => overrides[row.key]?.content)]);

    mutate({
      // One for the batch: the server records it on every message it sends.
      category: values?.category,
      subject: values?.subject,
      content,
      images,
      // The plain-text half of the batch's message, only when some text falls back to it.
      textContent: pendingSend?.usesBatchText ? pendingSend.batchText : undefined,
      // What the recipient list was drawn from, recorded on the batch for audit.
      dataSourceUrl: `${
        process.env.REACT_APP_API_URL
      }Utilities/CustomerMessage/Batch/Customers?${new URLSearchParams(
        toQueryPairs(listParams)
      ).toString()}`,
      messages: sending.map((row, index) => {
        const override = overrides[row.key];
        const custom = customRecipients[row.key];

        // Send is blocked while any included row is unresolved, so by here every one has an
        // answer; email is a defensive default rather than a decision.
        const channel = resolveRow(row).channel ?? 'email';

        return {
          entityId: row.id,
          // The server obeys this rather than working it out again.
          channel: channel === 'sms' ? 'Text' : 'Email',
          // Undefined, not [], for untouched rows - the server reads null as "use this customer's
          // default contacts", already partitioned by the channel above.
          to: custom ? addressesFor(custom.to, channel) : undefined,
          cc: custom ? addressesFor(custom.cc, channel) : undefined,
          subject: override?.subject,
          // Undefined for a row on the default message, as before; its pictures as references.
          content: rowContents[index],
          // Only a text carries one. An email row's derived text is never sent, so its length
          // cannot refuse the batch.
          textContent:
            channel === 'sms' ? pendingSend?.rowTexts[row.key] ?? override?.textContent : undefined,
        };
      }),
    });
  };

  // Stable identity, so the memoized list rows do not re-render when anything else in the dialog does.
  const toggleInclude = useCallback(
    (key: string) =>
      setExcluded((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    []
  );

  // Only the keys given - the rows the list shows - so a filtered view's All / None stays inside it.
  const includeAll = (include: boolean, keys: string[]) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => (include ? next.delete(key) : next.add(key)));
      return next;
    });

  if (isLoading) return <CasLoaderWithBackdrop />;
  if (isError) throw new Error(error.message);

  return (
    <>
      <CasBaseDialog
        open
        maxWidth="xl"
        onClose={onClose}
        title="Batch Send"
        actions={
          <>
            {invalidRowCount > 0 && (
              <Typography variant="caption" sx={{ color: 'error.main', mr: 'auto' }}>
                {invalidRowCount} enabled {invalidRowCount === 1 ? 'message is' : 'messages are'}{' '}
                invalid
                {/* Beside the disabled Send, where the sender looks when it will not go. */}
                {listView !== 'invalid' && (
                  <>
                    {' '}
                    <CasLink onClick={() => changeListView('invalid')}>Review</CasLink>
                  </>
                )}
              </Typography>
            )}
            <CasSecondaryButton onClick={onClose}>Cancel</CasSecondaryButton>
            <CasButton
              onClick={async () => {
                // On the SMS view the text is the message, so the emails are rewritten from it first.
                syncBodyFromSms();

                // Whatever row is open has not been stashed yet - Send from inside a row must
                // include the edits that are on screen.
                const committedOverrides = commitCurrent();

                // Subject is required only when some message is an email - a text has none - so it
                // is checked here, where the rows' channels are known, rather than in the schema.
                const subjectMissing =
                  sendsEmail && !String(formMethods?.getValues('subject') ?? '').trim();

                // The form owns the Default message's Subject/Body, so let it report its own errors
                // before the confirmation asks about anything else. The rows' messages come next.
                const valid = await formMethods?.trigger(DEFAULT_MESSAGE_FIELDS);

                if (subjectMissing) {
                  formMethods?.setError('subject', {
                    type: 'manual',
                    message: 'Subject is required.',
                  });
                }

                if (!valid || subjectMissing) {
                  // Category, Subject and Body live on the Default message, which may not be on
                  // screen. An error on a hidden field made Send look like it did nothing.
                  const bodyError = formMethods?.getFieldState('content').error;
                  const defaultFieldInvalid =
                    subjectMissing ||
                    !!bodyError ||
                    formMethods?.getFieldState('subject').invalid ||
                    formMethods?.getFieldState('category').invalid;

                  if (defaultFieldInvalid && selectedRow) selectItem(DEFAULT_KEY);

                  /*
                    Both views show the Subject whenever an email goes out, which a missing one
                    implies. The Body is the one field the SMS view does not show - there, the email
                    Body is the text's own translation, so its problem is the text's and is said
                    there, on the SMS view the sender is looking at.
                  */
                  if (bodyError && batchChannel === 'sms') {
                    const message =
                      bodyError.type === 'required' ? 'Body is required.' : bodyError.message;
                    formMethods?.setError('textContent', { type: 'manual', message });
                  }
                  return;
                }

                // Each row's own message as it will be sent - only the ticked rows': an unticked
                // row's message goes nowhere.
                const tooLong = findTooLongRow(
                  rows.filter((row) => included.has(row.key)).map((row) => row.key),
                  committedOverrides
                );

                if (tooLong) {
                  // Opened, so the row editor holds its message - and the form says what is wrong.
                  selectItem(tooLong);
                  await formMethods?.trigger(['rowSubject', 'rowContent']);

                  // A row going out as a text shows its SMS editor, not its Body - so, as on the
                  // Default message, the Body's problem is said there.
                  const bodyError = formMethods?.getFieldState('rowContent').error;
                  const row = rows.find((r) => r.key === tooLong);
                  if (bodyError && row && editedAsText(row)) {
                    formMethods?.setError('rowTextContent', {
                      type: 'manual',
                      message: bodyError.message,
                    });
                  }
                  return;
                }

                prepareSend(committedOverrides);
              }}
              disabled={included.size === 0 || invalidRowCount > 0 || smsBlocked || isPending}
            >
              Send {included.size} Messages
            </CasButton>
          </>
        }
      >
        <CasReactHookForm
          schema={batchComposeSchema}
          // No default content: this window has nothing to say on the sender's behalf. No default
          // Category either - picking one is the sender's call (see RHFMessageCategory).
          defaultValues={{
            category: null,
            subject: '',
            content: '',
            useDefault: true,
            rowSubject: '',
            to: [],
            cc: [],
            smsImages: [],
            rowTextContent: '',
            rowSmsImages: [],
          }}
          setFormMethods={setFormMethods}
        >
          <TestingSiteNote sx={{ mb: 1 }} />
          {/* Top of the window: a text needs the sender's own RingCentral line connected. */}
          <RingCentralConnectAlert sx={{ mb: 1 }} />
          {listWarning && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {listWarning}
            </Alert>
          )}
          {/* Above both panes: the reworded messages may be the default one or any row's. */}
          <BatchSmsAdjustmentsAlert
            changed={smsAdjusted}
            onClose={() => setSmsAdjusted([])}
            sx={{ mb: 1 }}
          />
          <Stack direction="row" sx={{ height: '70vh', minHeight: 0 }}>
            <Stack
              sx={{
                flex: '0 0 260px',
                minHeight: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
              }}
            >
              <BatchRowList
                terms={CUSTOMER_TERMS}
                rows={rows}
                visibleRows={visibleRows}
                listView={listView}
                onListViewChange={changeListView}
                invalidCount={invalidCount}
                excludedCount={excludedCount}
                included={included}
                getRowCustom={getRowCustom}
                resolveRow={resolveRow}
                selectedKey={selectedKey}
                onSelect={handleSelectItem}
                onToggleInclude={toggleInclude}
                onIncludeAll={includeAll}
              />
            </Stack>

            <Stack sx={{ flexGrow: 1, minWidth: 0, pl: 2, minHeight: 0 }}>
              {/* Pane header: what is open on the left, how to move between panes on the right. */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ pb: 1 }}>
                <Typography variant="subtitle2">
                  {selectedRowData ? selectedRowData.label : 'Default message'}
                </Typography>

                {/* Only on the batch's own message: a row has recipients, so its channel is
                    resolved from them rather than chosen here. This picks which view of the
                    message is being edited, and which channel a contact who states none is tried
                    on first. */}
                {!selectedRowData && (
                  <ToggleButtonGroup
                    value={batchChannel}
                    exclusive
                    size="small"
                    onChange={onBatchChannelChange}
                  >
                    {BATCH_CHANNEL_OPTIONS.map(({ id, name }) => (
                      <ToggleButton
                        key={id}
                        value={id}
                        sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}
                      >
                        {name}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}

                {/* Per-customer, so it belongs to a row and never to the batch's own message. */}
                {selectedRowData && (
                  <CustomerContactsButton
                    entityId={selectedRowData.id ?? undefined}
                    label="Edit Contacts"
                    onChanged={handleContactsChanged}
                  />
                )}

                {/* "default", not "default message": this puts the recipients back too, so it is
                    offered for either kind of customization. */}
                {(selectedCustom?.recipients ||
                  selectedCustom?.message ||
                  selectedCustom?.channel) && (
                  <CasLink onClick={handleRevert}>Revert to default</CasLink>
                )}

                {/* Walks the list beside it as it is filtered, default message first. */}
                <Stack sx={{ ml: 'auto' }}>
                  <CasItemNavigator
                    value={selectedKey}
                    onChange={handleSelectItem}
                    options={navigableKeys}
                    formatLabel={formatNavLabel}
                  />
                </Stack>
              </Stack>

              <Stack sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, overflowY: 'auto' }}>
                {/*
                  Both panes stay mounted and are toggled with display. Each owns a Lexical editor,
                  and Lexical only reads its initial state at mount - unmounting the row pane would
                  rebuild the editor (and its plugins and contexts) on every click in the list.
                */}
                <div style={{ display: selectedRow ? 'block' : 'none' }}>
                  {selectedRowData && (
                    <>
                      <BatchRowPane
                        rowKey={selectedRowData.key}
                        label={selectedRowData.label}
                        optionsEntity="Utilities/CustomerMessage/Email/Select"
                        optionsParams={{ entityId: selectedRowData.id ?? undefined }}
                        selectedTo={rowRecipients.to}
                        selectedCC={rowRecipients.cc}
                        contactsUpdate={contactsUpdate}
                        excluded={!included.has(selectedRowData.key)}
                        onUseDefaultChange={handleUseDefaultChange}
                        onReadyEditor={setOverrideEditor}
                        sendAs={rowChannels[selectedRowData.key] ?? 'auto'}
                        unresolvedReason={resolveRow(selectedRowData).reason}
                        onSendAsChange={handleRowSendAsChange}
                        smsEditing={rowSmsEditing}
                      />
                      {/* Inside the form, so it can watch what the pane above is editing. */}
                      <BatchOpenRowWatcher
                        rowKey={selectedRowData.key}
                        seededTo={rowRecipients.to}
                        seededCC={rowRecipients.cc}
                        onChange={setOpenRow}
                      />
                    </>
                  )}
                </div>

                <div style={{ display: selectedRow ? 'none' : 'block' }}>
                  {/* Above the message it classifies, and in both of its views. */}
                  <RHFMessageCategory />

                  <Typography
                    variant="caption"
                    // MUI maps caption to a <span>, so bottom padding on it does nothing - it needs
                    // display:block before any spacing applies.
                    sx={{ display: 'block', lineHeight: 1.3, color: 'text.secondary', mb: 1.5 }}
                  >
                    Sent to every {CUSTOMER_TERMS.noun} ticked on the left, unless that row has a
                    message of its own.
                  </Typography>

                  {/* A text has no subject. */}
                  {batchChannel === 'email' && (
                    <CasRHFTextField
                      name="subject"
                      label="Subject"
                      inputProps={{ maxLength: MAX_SUBJECT_LENGTH }}
                    />
                  )}

                  {/*
                    Hidden rather than unmounted, exactly like the two panes above: Lexical only
                    reads its initial state at mount. It is also the message's Email view: the SMS
                    view is translated from it, and carried back into it.
                  */}
                  <Box sx={{ display: batchChannel === 'email' ? undefined : 'none' }}>
                    <CasRHFLexicalRichTextEditor
                      mode="advanced"
                      name="content"
                      label="Body"
                      helperText={
                        <>
                          Your{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="always"
                            onClick={signatureDialog.setTrue}
                            // A <button> is vertical-align: middle and carries its own font metrics,
                            // which lifts it off the sentence's baseline.
                            sx={{
                              verticalAlign: 'baseline',
                              font: 'inherit',
                              letterSpacing: 'inherit',
                            }}
                          >
                            signature
                          </Link>{' '}
                          will be appended to the Body
                        </>
                      }
                      onReadyEditor={setMasterEditor}
                      onClear={clearBody}
                    />
                  </Box>

                  {batchChannel === 'sms' && (
                    <>
                      <SmsTextField picturesName="smsImages" />
                      <RingCentralFromNumber sx={{ mt: -1, mb: 1 }} />
                      {/*
                        The emails go out too, and this is theirs - the one part of the message
                        the text has no place for.
                      */}
                      {sendsEmail && (
                        <CasRHFTextField
                          name="subject"
                          label="Subject (for Emails only)"
                          inputProps={{ maxLength: MAX_SUBJECT_LENGTH }}
                        />
                      )}
                      <BatchSmsPictures />
                    </>
                  )}
                </div>
              </Stack>
            </Stack>
          </Stack>
        </CasReactHookForm>
      </CasBaseDialog>

      {signatureDialog.value && <MySignatureSettingsDialog onClose={signatureDialog.setFalse} />}

      {confirmDialog.value && (
        <CasConfirmationDialog
          open
          title="Send batch"
          confirmLabel={`Send ${included.size} Messages`}
          onCancel={confirmDialog.setFalse}
          onConfirm={handleSend}
          confirmDisabled={
            !!pendingSend?.tooLong.length ||
            !!pendingSend?.tooLarge.length ||
            !!pendingSend?.empty.length
          }
        >
          <div>
            {included.size === 1
              ? 'This sends 1 message.'
              : `This sends ${included.size} separate messages, one per ${CUSTOMER_TERMS.noun}.`}
          </div>
          {smsRows.length > 0 && (
            <div>
              {`${smsRows.length} of them ${
                smsRows.length === 1 ? 'goes out as a text' : 'go out as texts'
              }.`}
            </div>
          )}
          {rows.length > included.size && (
            <div>
              {`${rows.length - included.size} of the ${rows.length} listed will not be sent.`}
            </div>
          )}

          {/* Blocks the confirmation: the carrier would refuse these, after the batch was queued. */}
          {!!pendingSend?.tooLong.length && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                A text can be at most {MAX_SMS_TEXT_LENGTH} characters. Shorten these before sending:
              </Typography>
              {pendingSend.tooLong.map(({ label, length }) => (
                <Typography key={label} variant="caption" sx={{ display: 'block', pl: 1 }}>
                  {label}: {length} characters
                </Typography>
              ))}
            </Alert>
          )}

          {/* Blocks the confirmation too: the server refuses the whole batch over any one of these. */}
          {!!pendingSend?.empty.length && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {TEXT_NEEDS_MESSAGE_OR_PICTURE} These have no words and nothing attached:
              </Typography>
              {pendingSend.empty.map((label) => (
                <Typography key={label} variant="caption" sx={{ display: 'block', pl: 1 }}>
                  {label}
                </Typography>
              ))}
            </Alert>
          )}

          {/* Blocks the confirmation too: Microsoft or the carrier would refuse these after queueing. */}
          {!!pendingSend?.tooLarge.length && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                These messages have more images than they can carry. Remove an image or paste a
                smaller one:
              </Typography>
              {/* Keyed by both: a text can be over its bytes and its count at once. */}
              {pendingSend.tooLarge.map(({ label, problem }) => (
                <Typography
                  key={`${label}: ${problem}`}
                  variant="caption"
                  sx={{ display: 'block', pl: 1 }}
                >
                  {label}: {problem}
                </Typography>
              ))}
            </Alert>
          )}
        </CasConfirmationDialog>
      )}
    </>
  );
}
